import { useState } from 'react';
import { Modal } from '../components/folders/Modal';
import { PRIVACY_URL } from './constants';

/**
 * First-use AI consent (docs/15-ai-consent-privacy.md C1, copy appendix A).
 *
 * The copy is split out from the chrome deliberately: docs/24 P7 adds a second presentation
 * of this same gate inside the side panel, where a page-blocking modal would be hostile, and
 * both surfaces must show the *same words*. `AiConsentCopy` is that shared substance.
 */

const cls = {
  body: 'space-y-3 text-[13px] leading-relaxed text-body font-sans',
  privacyLink: 'mt-3 inline-block text-[12px] text-bronze-text underline underline-offset-2',
  error: 'mt-3 text-[12px] text-danger font-sans',
  actions: 'mt-5 flex justify-end gap-2',
  ghost:   'px-4 py-2 text-[11px] font-semibold rounded-token border border-line-strong cursor-pointer bg-transparent text-navy uppercase tracking-[0.08em] font-sans',
  primary: 'px-5 py-2 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed',
};

/**
 * The consent substance — appendix A, verbatim. One source, every surface.
 *
 * Names Steward as the host, as a literal, never the brand's `productName`: the assistant runs
 * in Steward's cloud whatever brand the workspace wears, and a white-labelled firm's name here
 * would tell its users the firm runs it (founder, 2026-09-30). The host was named "Crossmarc"
 * until the founder asked for "Steward" (2026-10-24, steward-app#2554; no consent version bump).
 * The second paragraph's three "never"s became "not", and "runs inside Steward's own cloud
 * environment" became "runs through Steward's own secure cloud account" (founder, 2026-10-28; no
 * version bump). The old line overstated it: requests leave from, are IAM-gated by and are
 * logged in Steward's AWS account, but the model executes in Bedrock's AWS-owned deployment
 * accounts. "Not kept" rests on the Bedrock account data retention mode being `none` in us-west-2
 * (set in dev, stage and prod 2026-10-29), an account setting outside CDK that no test here pins.
 *
 * `firm` is the provider's `firmConversation` (true in an advisory firm's own workspace,
 * steward-app#1815) and picks only the first paragraph. It inherits `isFirmWorkspace`'s errors,
 * and the known ones read as a family workspace (a firm missing its pack). The consent dialogs
 * themselves are not offered while the kind is unresolved (`AssistantProvider`'s
 * `kindUnresolved`). Acceptance is stored per variant (steward-app#2523), neither satisfying the
 * other.
 *
 * The retention paragraph is one sentence for both variants since 2026-10-26 (steward-app#1817,
 * founder-approved wording, verbatim): firm conversations are saved under the household rule,
 * so the firm variant's "aren't saved" line went, and `AI_FIRM_CONSENT_VERSION` went to 2 so
 * firm users re-consent. The family version was not bumped.
 */
export function AiConsentCopy({ firm }: { firm: boolean }) {
  return (
    <>
      <div className={cls.body}>
        <p>
          {firm
            ? "The AI assistant provides responses using your firm's records and, for clients you work with, the information they share with your firm."
            : 'The AI assistant provides responses using information from your workspace.'}
        </p>
        <p>
          The AI runs through Steward&apos;s own secure cloud account. Your data is not used
          to train AI models and is not kept by the AI provider after your answer is generated.
          Credential-vault secrets are not shared with the AI.
        </p>
        <p>
          Conversations are kept for a limited time after your last activity, and you can delete
          any conversation at any time. Other people in your workspace can&apos;t see your
          conversations.
        </p>
        <p>
          Answers are AI-generated and can be wrong. Verify important figures against your
          records.
        </p>
      </div>
      <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={cls.privacyLink}>
        Privacy policy
      </a>
    </>
  );
}

/** The two buttons, shared so the panel's inline card can't drift from the modal's wording. */
export function AiConsentActions({
  onAccept, onDecline, declineLabel = 'Maybe later',
}: {
  onAccept: () => Promise<void>;
  onDecline: () => void;
  declineLabel?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function accept() {
    setBusy(true);
    setFailed(false);
    try {
      await onAccept();
    } catch {
      // The acknowledgment was not stored (steward-app#2523), so say so rather than let the
      // button quietly reset to a state that looks like nothing happened.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    {failed && (
      <p role="alert" className={cls.error}>We could not save that just now. Please try again.</p>
    )}
    <div className={cls.actions}>
      <button type="button" onClick={onDecline} disabled={busy} className={cls.ghost}>
        {declineLabel}
      </button>
      <button type="button" onClick={() => void accept()} disabled={busy} className={cls.primary}>
        {busy ? 'Saving…' : 'I understand, start chatting'}
      </button>
    </div>
    </>
  );
}

/** Shown in place of the consent dialog when the workspace kind could not be read, so the
 *  right dialog cannot be chosen (steward-app#2523). Copy proposed 2026-10-23, pending Matt's review. */
export function AiKindNotice({ onRetry, focusRef }: {
  onRetry: () => Promise<void>;
  /** The panel focuses the message when it appears, as it does the consent card's heading. */
  focusRef?: React.Ref<HTMLParagraphElement>;
}) {
  const [busy, setBusy] = useState(false);
  async function retry() {
    setBusy(true);
    try { await onRetry(); } finally { setBusy(false); }
  }
  return (
    <div className={cls.body} data-testid="ai-kind-notice">
      <p role="status" tabIndex={-1} ref={focusRef}>We could not load your workspace details just now.</p>
      <div className={cls.actions}>
        <button type="button" onClick={() => void retry()} disabled={busy} className={cls.primary}>
          Try again
        </button>
      </div>
    </div>
  );
}

/** Blocking first-use consent notice for the full `/ask` page (docs/15 C1/appendix A). Backdrop,
 *  Escape, and the close button all behave like "Maybe later" — there is no dismiss path that
 *  leaves the assistant usable without either acknowledging or leaving the page. */
export function AiConsentModal({ onAccept, onDecline, firm }: {
  onAccept: () => Promise<void>; onDecline: () => void; firm: boolean;
}) {
  return (
    <Modal titleText="Before you use the assistant" onClose={onDecline}>
      <AiConsentCopy firm={firm} />
      <AiConsentActions onAccept={onAccept} onDecline={onDecline} />
    </Modal>
  );
}
